import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { SearchField } from "@/components/common/SearchField/SearchField"
import { toast } from "@/components/common/Toast"
import { FacetedFilter } from "@/components/feature/KnowledgebaseFilters/FacetedFilter"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconTrash } from "@tabler/icons-react"
import {
  AlertCircle,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Filter,
  Inbox,
  Loader2,
  MoreVertical,
  Package,
  Trash2,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router"
import { toastApiError } from "../../api/apiError"
import type {
  QuestionnaireListParams,
  QuestionnaireRow,
  QuestionnaireStatus,
} from "../../api/customerRequestsApi"
import {
  daysUntilLabel,
  dueDateTone,
  useCustomers,
  useDeleteQuestionnaire,
  useQuestionnaires,
  useQuestionnaireStats,
} from "../../api/customerRequestsApi"
import { useMcTableColumnWidths } from "../../hooks/useMcTableColumnWidths"
import { McPill } from "../shared/McPill"
import { McTable, McTd, McTr } from "../shared/McTable"
import { StatGrid } from "../shared/StatGrid"
import {
  canonicalStepForStatus,
  isFailedStatus,
  isInFlightStatus,
  type ProcessingStatus,
} from "./processing/processingSteps"

type TimeRange = "" | "30" | "90" | "180"
const PAGE_SIZE = 25
const COLUMN_IDS = [
  "id",
  "customer",
  "document",
  "framework",
  "articles",
  "due",
  "progress",
  "actions",
]

// 25% over the ~150px an unpinned column gets under table-fixed's equal-share
// fallback (8 columns).
const CUSTOMER_REQUESTS_INBOX_DEFAULT_COLUMN_WIDTHS = {
  customer: 188,
  document: 188,
  framework: 188,
}

interface InboxViewProps {
  onOpen: (row: QuestionnaireRow) => void
}

const CANONICAL_STEP_LABEL_KEYS = {
  mapping: "MC_QP_STEP_MAPPING_TITLE",
  reconcile: "MC_QP_STEP_RECONCILE_TITLE",
  gaps: "MC_QP_STEP_GAPS_TITLE",
  review: "MC_QP_STEP_REVIEW_TITLE",
} as const

/** Renders the answering-progress pill for a row's `processingStatus` — null before Phase A starts. */
function ProcessingProgressPill({
  status,
  translate,
}: {
  status: ProcessingStatus
  translate: ReturnType<typeof useTranslation>["translate"]
}) {
  if (status === null) return null

  if (isFailedStatus(status)) {
    return (
      <McPill tone="danger" icon={AlertCircle}>
        {translate("MC_Q_PSTATUS_FAILED")}
      </McPill>
    )
  }
  if (status === "sent") {
    return <McPill tone="success">{translate("MC_Q_PSTATUS_SENT")}</McPill>
  }
  if (status === "answered") {
    return (
      <McPill tone="success" icon={Check}>
        {translate(CANONICAL_STEP_LABEL_KEYS.review)}
      </McPill>
    )
  }
  if (isInFlightStatus(status)) {
    return (
      <McPill tone="info">
        <Loader2 size={11} className="animate-spin" />
        {translate(CANONICAL_STEP_LABEL_KEYS[canonicalStepForStatus(status)])}
      </McPill>
    )
  }
  // "_ready" states — waiting on the user's next action in the process flow.
  return (
    <McPill tone="attention">
      {translate(CANONICAL_STEP_LABEL_KEYS[canonicalStepForStatus(status)])}
    </McPill>
  )
}

// Dashboard tile labels for the `?bucket=` deep-link chip — reuses the Task 8
// pipeline-tile keys rather than adding new ones (answering/waiting/gaps/
// overdue mirror the CoverageWidget tile names exactly). `open` has no
// pipeline tile of its own (it's the Open widget's header link, not a
// CoverageWidget bucket), so it reuses that widget's own title key instead.
const BUCKET_LABEL_KEYS: Partial<Record<string, TranslationKeys>> = {
  open: "MC_CSD_W_OPEN",
  answering: "MC_CSD_TILE_ANSWERING",
  waiting: "MC_CSD_TILE_WAITING",
  gaps: "MC_CSD_TILE_GAPS",
  overdue: "MC_CSD_TILE_OVERDUE",
}

/** Map the period filter to a local YYYY-MM-DD `from_date` for the API. */
function periodToFromDate(period: TimeRange): string | undefined {
  if (!period) return undefined
  const d = new Date()
  d.setDate(d.getDate() - Number(period))
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export function InboxView({ onOpen }: InboxViewProps) {
  const { translate } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Dashboard deep-link params. `bucket` has no local widget of its own — it's
  // read straight from the URL every render (like the supplier declarations
  // list's `lifecycle` param) and forwarded verbatim to the backend, which
  // validates it. `customer` maps onto the *existing* `filterCustomer` facet
  // state, seeded once on mount (see useState initializer below) so it plays
  // nicely with the user reselecting the Customers facet afterwards.
  const qBucket = searchParams.get("bucket") ?? ""
  const qCustomer = searchParams.get("customer") ?? ""
  const hasUrlFilter = !!(qBucket || qCustomer)

  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [filterCustomer, setFilterCustomer] = useState(
    () => searchParams.get("customer") ?? "",
  )
  const [filterStatus, setFilterStatus] = useState<QuestionnaireStatus | "">("")
  const [filterTimeRange, setFilterTimeRange] = useState<TimeRange>("")
  const [filterOverdue, setFilterOverdue] = useState(false)
  const [page, setPage] = useState(1)
  const [rowToDelete, setRowToDelete] = useState<QuestionnaireRow | null>(null)

  const deleteQuestionnaire = useDeleteQuestionnaire()

  // Keep `filterCustomer` in sync with `?customer=` after mount (browser
  // back/forward, future in-app deep links) — the useState initializer above
  const [appliedCustomer, setAppliedCustomer] = useState(qCustomer)
  if (qCustomer !== appliedCustomer) {
    setAppliedCustomer(qCustomer)
    setFilterCustomer(qCustomer)
    setPage(1)
  }

  // Strip dashboard deep-link params from the URL (replacing history so
  // back/forward doesn't resurrect a stale filter). Single place the strip
  // logic lives; every path that supersedes a URL-seeded filter reuses it —
  // the chip's own × affordance, a manual re-pick of the Customers facet
  // (consumes only `customer` — the user took manual control of that one
  // filter), and the generic Reset (consumes both). `bucket` has no facet of
  // its own, so the ONLY ways it's ever cleared are the chip's × and the
  // generic Reset — never as a side effect of some other control.
  const stripUrlFilterParams = (keys: string[]) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        keys.forEach((k) => next.delete(k))
        return next
      },
      { replace: true },
    )
  }

  // Clear the dashboard deep-link filters (chip's × affordance) — mirrors
  // DeclarationList.clearUrlFilters exactly, minus the params this view
  // doesn't have.
  const clearUrlFilters = () => {
    if (qCustomer) setFilterCustomer("")
    setAppliedCustomer("")
    stripUrlFilterParams(["bucket", "customer"])
    setPage(1)
  }

  const [columnWidths, setColumnWidths] = useMcTableColumnWidths(
    "customerRequestsInbox",
    CUSTOMER_REQUESTS_INBOX_DEFAULT_COLUMN_WIDTHS,
  )

  // Debounce the search box before it hits the API.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const params: QuestionnaireListParams = useMemo(
    () => ({
      status: filterStatus || undefined,
      customerId: filterCustomer || undefined,
      bucket: qBucket || undefined,
      fromDate: periodToFromDate(filterTimeRange),
      overdue: filterOverdue || undefined,
      search: search || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [
      filterStatus,
      filterCustomer,
      qBucket,
      filterTimeRange,
      filterOverdue,
      search,
      page,
    ],
  )

  const { data: stats } = useQuestionnaireStats()
  const { data: pageData, isLoading } = useQuestionnaires(params)
  const { data: customers = [] } = useCustomers()

  const rows = pageData?.items ?? []
  const total = pageData?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const customerOptions = customers.map((c) => ({ label: c.name, value: c.id }))
  const statusOptions = [
    { value: "extracting", label: translate("MC_Q_STATUS_EXTRACTING") },
    { value: "needs_confirm", label: translate("MC_Q_STATUS_NEEDS_CONFIRM") },
    { value: "stored_pending", label: translate("MC_Q_STATUS_STORED_PENDING") },
    { value: "processed", label: translate("MC_Q_STATUS_PROCESSED") },
    {
      value: "extraction_failed",
      label: translate("MC_Q_STATUS_EXTRACTION_FAILED"),
    },
  ]
  const periodOptions = [
    { value: "30", label: translate("MC_Q_FILTER_PERIOD_30") },
    { value: "90", label: translate("MC_Q_FILTER_PERIOD") },
    { value: "180", label: translate("MC_Q_FILTER_PERIOD_180") },
  ]

  const hasActiveFilters =
    !!filterCustomer || !!filterStatus || !!filterTimeRange || filterOverdue

  const resetFilters = () => {
    setFilterCustomer("")
    setFilterStatus("")
    setFilterTimeRange("")
    setFilterOverdue(false)
    setAppliedCustomer("")
    // The generic reset also supersedes any active deep link — strip both
    // params so the chip doesn't linger after everything else has cleared.
    stripUrlFilterParams(["bucket", "customer"])
    setPage(1)
  }

  // Chip content for the active dashboard deep-link filter(s) — bucket shows
  // its dashboard tile label, customer resolves to a name (falls back to the
  // raw id while `useCustomers` is still loading).
  const urlFilterLabels = [
    qBucket &&
      (BUCKET_LABEL_KEYS[qBucket]
        ? translate(BUCKET_LABEL_KEYS[qBucket]!)
        : qBucket),
    qCustomer && (customers.find((c) => c.id === qCustomer)?.name ?? qCustomer),
  ].filter(Boolean)

  const handleConfirmDelete = () => {
    if (!rowToDelete) return
    deleteQuestionnaire.mutate(rowToDelete.id, {
      onSuccess: () => {
        toast.success(translate("MC_Q_CANCEL_INTAKE_SUCCESS"))
        setRowToDelete(null)
      },
      onError: (err) => {
        toastApiError(err, translate, "MC_Q_CANCEL_INTAKE_ERROR")
      },
    })
  }

  const columns = [
    translate("MC_Q_COL_ID"),
    translate("MC_Q_COL_CUSTOMER"),
    translate("MC_Q_COL_DOCUMENT"),
    translate("MC_Q_COL_FRAMEWORK"),
    translate("MC_Q_COL_ARTICLES"),
    translate("MC_Q_COL_DUE"),
    translate("MC_Q_COL_PROGRESS"),
    "",
  ]

  return (
    <div className="flex flex-col gap-[18px]">
      <StatGrid
        items={[
          {
            label: translate("MC_Q_STAT_RECEIVED"),
            value: stats?.received ?? 0,
            icon: Inbox,
          },
          {
            label: translate("MC_Q_STAT_PENDING"),
            value: stats?.pending ?? 0,
            icon: Loader2,
            tone: "warning",
          },
          {
            label: translate("MC_Q_STAT_STORED"),
            value: stats?.stored ?? 0,
            icon: Package,
          },
          {
            label: translate("MC_Q_STAT_PROCESSED"),
            value: stats?.processed ?? 0,
            icon: CheckCircle,
            tone: "success",
          },
          // Only surface the failed tile when extraction has actually failed,
          // so it draws attention instead of sitting at a permanent zero.
          ...(stats?.failed
            ? [
                {
                  label: translate("MC_Q_STAT_FAILED"),
                  value: stats.failed,
                  icon: AlertCircle,
                  tone: "danger" as const,
                },
              ]
            : []),
        ]}
      />

      {/* Search + faceted filters (mirrors the supplier declarations toolbar). */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          value={searchInput}
          onChange={setSearchInput}
          placeholder={translate("MC_Q_SEARCH_PLACEHOLDER")}
          onClear={() => setSearchInput("")}
        />
        <FacetedFilter
          title={translate("MC_Q_FILTER_CUSTOMERS")}
          options={customerOptions}
          selected={filterCustomer ? [filterCustomer] : []}
          onSelectionChange={(vals) => {
            setFilterCustomer(vals[vals.length - 1] ?? "")

            setAppliedCustomer("")
            stripUrlFilterParams(["customer"])
            setPage(1)
          }}
        />
        <FacetedFilter
          title={translate("MC_Q_FILTER_STATUSES")}
          options={statusOptions}
          selected={filterStatus ? [filterStatus] : []}
          onSelectionChange={(vals) => {
            setFilterStatus(
              (vals[vals.length - 1] as QuestionnaireStatus) ?? "",
            )
            setPage(1)
          }}
        />
        <FacetedFilter
          title={translate("MC_Q_FILTER_PERIOD_TITLE")}
          options={periodOptions}
          selected={filterTimeRange ? [filterTimeRange] : []}
          onSelectionChange={(vals) => {
            setFilterTimeRange((vals[vals.length - 1] as TimeRange) ?? "")
            setPage(1)
          }}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setFilterOverdue((v) => !v)
            setPage(1)
          }}
          className={cn(
            "border-dashed font-normal",
            filterOverdue && "border-solid border-yellow1 text-yellow1",
          )}
        >
          <Clock className="size-4" />
          {translate("MC_Q_FILTER_OVERDUE")}
        </Button>
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="ml-1 text-[11px] text-yellow1 hover:underline"
          >
            {translate("MC_DECL_FILTER_CLEAR")}
          </button>
        )}
      </div>

      {/* Dashboard deep-link filter chip — mirrors the supplier
          DeclarationList's `hasUrlFilter` chip pattern exactly. */}
      {hasUrlFilter && (
        <div className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-grey5">
          <Filter size={11} className="shrink-0" />
          <span className="truncate">
            {translate("MC_DECL_FILTER_PREFIX")}: {urlFilterLabels.join(", ")}
          </span>
          <span className="text-grey4">·</span>
          <button
            type="button"
            onClick={clearUrlFilters}
            className="shrink-0 text-grey5 underline-offset-2 transition-colors hover:text-white hover:underline"
          >
            {translate("MC_DECL_FILTER_CLEAR")}
          </button>
        </div>
      )}

      <McTable
        headers={columns}
        className="bg-grey3"
        columnIds={COLUMN_IDS}
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
      >
        <tbody>
          {rows.map((r) => (
            <McTr key={r.id} onClick={() => onOpen(r)}>
              <McTd variant="muted" mono>
                {r.requestId}
              </McTd>
              <McTd variant="primary">{r.customer}</McTd>
              <McTd variant="primary">
                <div className="flex items-center gap-2">
                  <FileText size={15} className="shrink-0 text-grey5" />
                  <span className="min-w-0 flex-1 truncate">
                    {r.doc ?? "—"}
                  </span>
                </div>
              </McTd>
              <McTd variant="muted">
                <span className="block truncate">
                  {r.frameworks.join(" · ")}
                </span>
              </McTd>
              <McTd variant="primary" mono>
                {r.articles}
              </McTd>
              <McTd mono className="whitespace-nowrap">
                <span
                  className={
                    dueDateTone(r.due) === "danger"
                      ? "text-red1"
                      : dueDateTone(r.due) === "warning"
                        ? "text-warning"
                        : "text-grey5"
                  }
                >
                  {r.due || "—"}
                  {r.due && (
                    <span className="ml-1.5 text-[10px]">
                      ({daysUntilLabel(r.due, translate)})
                    </span>
                  )}
                </span>
              </McTd>
              <McTd>
                {r.processingStatus ? (
                  <ProcessingProgressPill
                    status={r.processingStatus}
                    translate={translate}
                  />
                ) : r.status === "processed" ? (
                  <McPill tone="success" icon={Check}>
                    {translate("MC_Q_STATUS_PROCESSED")}
                  </McPill>
                ) : r.status === "stored_pending" ? (
                  <McPill tone="muted" icon={Package}>
                    {translate("MC_Q_STATUS_STORED_PENDING")}
                  </McPill>
                ) : r.status === "needs_confirm" ? (
                  <McPill tone="attention" icon={AlertCircle}>
                    {translate("MC_Q_STATUS_NEEDS_CONFIRM")}
                  </McPill>
                ) : r.status === "extracting" ? (
                  <McPill tone="info">
                    <Loader2 size={11} className="animate-spin" />
                    {translate("MC_Q_STATUS_EXTRACTING")}
                  </McPill>
                ) : r.status === "extraction_failed" ? (
                  <McPill tone="danger" icon={AlertCircle}>
                    {translate("MC_Q_STATUS_EXTRACTION_FAILED")}
                  </McPill>
                ) : null}
              </McTd>
              <McTd className="whitespace-nowrap text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-grey5"
                      aria-label={translate("MC_Q_MORE_ACTIONS")}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical size={15} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenuItem
                      className="text-red1 focus:text-red1"
                      onSelect={() => setRowToDelete(r)}
                    >
                      <Trash2 size={14} />
                      {translate("MC_Q_MENU_REMOVE")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </McTd>
            </McTr>
          ))}
          {!rows.length && (
            <McTr>
              <McTd
                variant="muted"
                colSpan={columns.length}
                className="py-6 text-center"
              >
                {isLoading
                  ? translate("MC_Q_INBOX_LOADING")
                  : translate("MC_Q_INBOX_EMPTY")}
              </McTd>
            </McTr>
          )}
        </tbody>
      </McTable>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[12px] text-grey5">
        <span>
          {translate("MC_Q_PAGINATION_INFO", {
            page: total === 0 ? 0 : page,
            totalPages,
            total,
          })}
        </span>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-grey4 px-2"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={14} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-grey4 px-2"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>

      {rowToDelete && (
        <ConfirmDialog
          variant="danger"
          icon={IconTrash}
          title={translate("MC_Q_CANCEL_INTAKE_CONFIRM_TITLE")}
          message={translate("MC_Q_CANCEL_INTAKE_CONFIRM_DESC")}
          confirmLabel={translate("MC_Q_ACTION_CANCEL_INTAKE")}
          cancelLabel={translate("MC_Q_DELETE_DIALOG_CANCEL")}
          isLoading={deleteQuestionnaire.isPending}
          onConfirm={handleConfirmDelete}
          onCancel={() => setRowToDelete(null)}
        />
      )}
    </div>
  )
}
